import { Link } from "react-router-dom";
import {
  Mail,
  Download,
  ArrowUpRight,
  Linkedin,
  Instagram,
  Facebook,
  ArrowRight,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Seo from "@/components/seo/Seo";
import { AppStoreBadge, GooglePlayBadge } from "@/components/brand/StoreBadges";
import {
  COMPANY,
  SAFE_TO_SPEND,
  EMBER360,
  BRAND_COLORS,
  BRAND_ASSETS,
  HERO_BANNER,
  APP_SCREENSHOTS,
  FOUNDER_PHOTOS,
  KEY_FEATURES,
  FAQ,
  TESTIMONIALS,
  PRESS_RELEASE,
} from "@/data/pressKit";

export default function Press() {
  const [copied, setCopied] = useState(null);
  const copy = (label, text) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(null), 1200);
  };

  return (
    <>
      <Seo
        title="Press Kit & Brand Assets"
        description="Media kit for AtlasForge Financial and Safe to Spend 365. Download logos, high-resolution app screenshots, founder photos, brand guidelines, and the launch press release."
        path="/press"
        image={HERO_BANNER}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "AtlasForge Financial Press Kit",
            "url": "https://atlasforgefinancial.com/press",
            "publisher": {
              "@type": "Organization",
              "name": COMPANY.name,
              "logo": BRAND_ASSETS.sts_logo,
            },
          },
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A1128] text-white">
        <img
          src={HERO_BANNER}
          alt="Safe to Spend 365 — Stop guessing, start spending with confidence"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/70 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-24 pb-20 md:pt-32 md:pb-28">
          <p className="text-[12px] uppercase tracking-[0.18em] text-white/70">
            Press & Media
          </p>
          <h1
            data-testid="press-hero-title"
            className="font-display mt-3 text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.02] max-w-3xl"
          >
            The AtlasForge Financial press kit.
          </h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-2xl">
            Everything media, creators, and partners need to write about Safe to Spend 365
            and the AtlasForge platform — assets, story, screenshots, and
            interview-ready facts.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="press-hero-cta">
            <a
              href="#assets"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A1128] px-6 py-3 text-[14px] font-medium hover:bg-white/90 transition-colors"
            >
              Download assets <Download className="h-4 w-4" />
            </a>
            <a
              href={`mailto:${COMPANY.media_contact.email}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-[14px] font-medium hover:bg-white/10 transition-colors"
            >
              <Mail className="h-4 w-4" /> Media inquiries
            </a>
          </div>
        </div>
      </section>

      {/* ABOUT SAFE TO SPEND */}
      <Section id="about" eyebrow="About Safe to Spend 365" title="One number. Every day. Zero guesswork.">
        <div className="grid lg:grid-cols-2 gap-10 mt-8">
          <div className="space-y-4 text-[15.5px] text-slate-600 leading-relaxed">
            <p>
              <strong className="text-[#0A1128]">{SAFE_TO_SPEND.one_liner}</strong>
            </p>
            <p>
              <strong className="text-[#0A1128]">Why we built it.</strong> Traditional budgeting apps
              ask users to categorize dozens of transactions per week. 68% of
              users abandon them within 90 days. Safe to Spend 365 replaces the
              ritual with a single answer: "how much can I spend today without
              breaking anything?"
            </p>
            <p>
              <strong className="text-[#0A1128]">The problem it solves.</strong> 63% of Americans live
              paycheck to paycheck — including 48% of six-figure earners. The
              root cause isn't income; it's the invisible timing gap between
              account balance and upcoming commitments. Safe to Spend 365 closes
              that gap in real time.
            </p>
            <p>
              <strong className="text-[#0A1128]">Who it's for.</strong> Anyone who has tried three
              budgeting apps and abandoned all of them. Anyone who's ever
              overdrafted because "the number looked fine." Anyone who wants
              financial confidence without the mental overhead of a spreadsheet.
            </p>
            <p>
              <strong className="text-[#0A1128]">What makes it different.</strong> One number, not
              thirty categories. Real-time updates, not weekly reconciliation.
              An AI Coach (Autopilot, powered by Claude Sonnet 4.5) that
              answers plain-English questions about your money — without ever
              touching it.
            </p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-[#F7F9FC] p-8 self-start">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Fast facts</p>
            <dl className="mt-4 space-y-3 text-[14px]">
              <FactRow label="Product" value={SAFE_TO_SPEND.name} />
              <FactRow label="Category" value="Personal Finance / Consumer Fintech" />
              <FactRow label="Pricing" value={SAFE_TO_SPEND.pricing} />
              <FactRow label="Platforms" value={SAFE_TO_SPEND.platforms.join(" · ")} />
              <FactRow label="Bank connections" value="Plaid — 12,000+ US institutions" />
              <FactRow label="AI provider" value="Anthropic Claude Sonnet 4.5" />
              <FactRow label="Encryption" value="TLS 1.3 in transit · AES-256 at rest" />
            </dl>
            <div className="mt-6 flex flex-wrap gap-3">
              <AppStoreBadge product="safe-to-spend" />
              <GooglePlayBadge product="safe-to-spend" />
            </div>
          </div>
        </div>
      </Section>

      {/* ABOUT EMBER360 */}
      <Section id="about-ember" eyebrow="About Ember360" title="Money that moves with your circle.">
        <div className="grid lg:grid-cols-2 gap-10 mt-8">
          <div className="space-y-4 text-[15.5px] text-slate-600 leading-relaxed">
            <p>
              <strong className="text-[#0A1128]">{EMBER360.one_liner}</strong>
            </p>
            <p>
              <strong className="text-[#0A1128]">Why we built it.</strong> Banking,
              portfolios, subscriptions, and cash-flow forecasting still live
              across four or five disconnected apps for most people. Ember360
              collapses them into a single AI-powered view of your money.
            </p>
            <p>
              <strong className="text-[#0A1128]">Who it&apos;s for.</strong> Individuals
              and households who want one place to see every checking, savings,
              credit, brokerage, and crypto account — with intelligent nudges
              and forecasting built in.
            </p>
            <p>
              <strong className="text-[#0A1128]">What makes it different.</strong>{" "}
              Ember360 pairs Plaid-secured account aggregation with an AI money
              coach you can actually ask questions. Subscriptions are detected
              automatically, portfolios refresh live, and 30-day cash-flow
              forecasts help you see what&apos;s coming before it hits.
            </p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-[#F7F9FC] p-8 self-start">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Fast facts</p>
            <dl className="mt-4 space-y-3 text-[14px]">
              <FactRow label="Product" value={EMBER360.name} />
              <FactRow label="Category" value="Consumer Fintech / AI Money App" />
              <FactRow label="Pricing" value={EMBER360.pricing} />
              <FactRow label="Platforms" value={EMBER360.platforms.join(" · ")} />
              <FactRow label="Bank connections" value="Plaid — 12,000+ US institutions" />
              <FactRow label="AI provider" value="Anthropic Claude Sonnet 4.5" />
              <FactRow label="Encryption" value="TLS 1.3 in transit · AES-256 at rest" />
            </dl>
            <div className="mt-6 flex flex-wrap gap-3">
              <AppStoreBadge product="ember" />
              <GooglePlayBadge product="ember" />
            </div>
          </div>
        </div>
      </Section>

      {/* FOUNDER STORY */}
      <Section id="founder" eyebrow="Founder story" title="The frustration that started AtlasForge.">
        <div className="grid lg:grid-cols-12 gap-10 mt-8 items-start">
          <div className="lg:col-span-4">
            {FOUNDER_PHOTOS.map((p) => (
              <img
                key={p.src}
                src={p.src}
                alt={p.alt}
                loading="lazy"
                decoding="async"
                className="w-full rounded-3xl object-cover aspect-square af-shadow-card"
                data-testid="press-founder-photo"
              />
            ))}
          </div>
          <div className="lg:col-span-8 space-y-4 text-[15.5px] text-slate-600 leading-relaxed">
            <p>
              I've spent my career as a budget analyst — the person institutions
              call when the numbers don't add up. I built spreadsheets, ran
              cash-flow models, and forecast payroll for organizations moving
              millions of dollars a month. Then I'd come home and watch the
              people closest to me — friends, family, my own extended circle —
              have no idea where their money was going, and overdraft their
              accounts three days before payday.
            </p>
            <p>
              That contrast wore me down. If the same math I ran professionally
              for a large budget could keep an organization solvent, why
              couldn't we give ordinary households one honest number that told
              them what they could actually afford this week?
            </p>
            <p>
              The frustration was clarifying: every American bank in 2026 shows
              an "available balance" that ignores the rent due Friday, the
              auto-pay hitting Monday, and the annual insurance premium landing
              next week. People weren't overspending — they were spending
              against a number that lied to them. That's not a discipline
              problem. It's a design problem.
            </p>
            <p>
              Safe to Spend 365 was born the night I sat down with a close family
              member to reverse-engineer a stack of overdraft fees she couldn't
              afford. Charge by charge, we found forgotten subscriptions,
              mistimed autopays, and a "balance" that had been misleading her
              for months. I opened my laptop and thought: "There is a single
              number that would have prevented every one of these. Why doesn't
              her bank just show it?" A weekend later, the first prototype ran
              on my phone.
            </p>
            <p>
              Our mission is simple: <strong className="text-[#0A1128]">give every household in
              America a single, honest number they can trust every day.</strong> No
              categories, no guilt, no gamification tricks. Just the math a
              budget analyst would run, done correctly, updated in real time.
              If we do our job right, financial confidence stops being a
              privilege of people who love spreadsheets — and becomes the
              default for everyone else.
            </p>
            <p className="text-[13px] text-slate-500 pt-3">
              — {COMPANY.founder}, Founder · AtlasForge Financial
            </p>
          </div>
        </div>
      </Section>

      {/* SCREENSHOTS */}
      <Section id="screenshots" eyebrow="App screenshots" title="Press-quality product imagery." dark>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {APP_SCREENSHOTS.map((s) => (
            <a
              key={s.src}
              href={s.src}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`press-screenshot-${s.caption.toLowerCase().replace(/\s+/g, "-")}`}
              className="group block relative rounded-2xl overflow-hidden bg-[#0F172A] border border-white/10"
            >
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover aspect-[9/19] group-hover:scale-[1.02] transition-transform"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between">
                <span className="text-[12px] font-medium text-white">{s.caption}</span>
                <Download className="h-3.5 w-3.5 text-white/80 group-hover:text-white" />
              </div>
            </a>
          ))}
        </div>
        <p className="mt-4 text-[12.5px] text-white/60">
          Right-click → Save As, or click any screenshot to open the full-resolution PNG.
        </p>
      </Section>

      {/* KEY FEATURES */}
      <Section id="features" eyebrow="Key features" title="What makes Safe to Spend 365 different.">
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {KEY_FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-slate-200 bg-white p-6 af-shadow-card"
            >
              <Sparkles className="h-5 w-5 text-[#00C48C] mb-3" />
              <p className="font-display text-[16px] text-[#0A1128]">{f.title}</p>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* COMPANY FACTS */}
      <Section id="facts" eyebrow="Company facts" title="Everything a journalist might ask.">
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 af-shadow-card">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">The company</p>
            <dl className="mt-4 space-y-3 text-[14px]" data-testid="press-company-facts">
              <FactRow label="Company" value={COMPANY.name} />
              <FactRow label="Founded" value={COMPANY.founded} />
              <FactRow label="Headquarters" value={COMPANY.hq} />
              <FactRow label="Founder" value={COMPANY.founder} />
              <FactRow label="Industry" value={COMPANY.industry} />
              <FactRow label="Employees" value={COMPANY.employees} />
              <FactRow label="Website" value={COMPANY.website} />
            </dl>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-[#0A1128] text-white p-8">
            <p className="text-[12px] uppercase tracking-[0.18em] text-white/60">Social</p>
            <ul className="mt-4 space-y-3">
              <SocialRow href={COMPANY.socials.linkedin} label="LinkedIn" Icon={Linkedin} />
              <SocialRow href={COMPANY.socials.instagram} label="Instagram" Icon={Instagram} />
              <SocialRow href={COMPANY.socials.facebook} label="Facebook" Icon={Facebook} />
            </ul>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-[12px] uppercase tracking-[0.18em] text-white/60">Media contact</p>
              <p className="mt-3 text-[15px] font-medium">{COMPANY.media_contact.name}</p>
              <a
                href={`mailto:${COMPANY.media_contact.email}`}
                className="inline-flex items-center gap-1.5 mt-1 text-[14px] text-[#00C48C] hover:underline"
                data-testid="press-media-email"
              >
                {COMPANY.media_contact.email}
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* BRAND COLORS */}
      <Section id="brand" eyebrow="Brand system" title="Colors, fonts, and safe-use rules.">
        <div className="mt-8 grid md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="press-brand-colors">
          {BRAND_COLORS.map((c) => (
            <button
              key={c.hex}
              onClick={() => copy(c.hex, c.hex)}
              className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#0A1128] transition-colors af-shadow-card"
            >
              <div
                className="h-16 rounded-lg mb-3 border border-slate-100"
                style={{ background: c.hex }}
              />
              <p className="text-[12px] uppercase tracking-[0.14em] text-slate-500">{c.role}</p>
              <p className="mt-1 font-display text-[15px] text-[#0A1128]">{c.name}</p>
              <p className="mt-1 text-[12.5px] font-mono text-slate-600 flex items-center gap-1.5">
                {c.hex}
                {copied === c.hex ? (
                  <Check className="h-3 w-3 text-[#00C48C]" />
                ) : (
                  <Copy className="h-3 w-3 text-slate-400" />
                )}
              </p>
            </button>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-slate-200 bg-[#F7F9FC] p-6 text-[13.5px] text-slate-600 leading-relaxed">
          <p><strong className="text-[#0A1128]">Type system:</strong> Display · Cabinet Grotesk (fallback Manrope, system-ui). Body · Inter / system-ui. Monospace · JetBrains Mono.</p>
          <p className="mt-3"><strong className="text-[#0A1128]">Do:</strong> Use logos on white, Cloud White, or the Deep Navy background only. Preserve at least 20px of clear space around every logo. Match Emerald Signal (#00C48C) with the Safe to Spend 365 product only.</p>
          <p className="mt-3"><strong className="text-[#0A1128]">Don't:</strong> Stretch, recolor, or add drop shadows to the logo. Never overlay text on the logomark. Never use the Ember Coral accent alongside the Safe to Spend 365 palette in the same layout.</p>
        </div>
      </Section>

      {/* DOWNLOADABLE ASSETS */}
      <Section id="assets" eyebrow="Downloadable assets" title="One-click downloads." dark>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AssetCard
            title="AtlasForge — Safe to Spend 365 Logo (PNG)"
            url={BRAND_ASSETS.atlas_logo_color}
            note="Color, transparent background"
          />
          <AssetCard
            title="Safe to Spend 365 Banking (Marketing Logo)"
            url={BRAND_ASSETS.atlas_logo_full}
            note="Extended horizontal lockup"
          />
          <AssetCard
            title="Ember360 — Product Logo (PNG)"
            url={BRAND_ASSETS.ember_logo}
            note="Ember360 product mark"
          />
          <AssetCard
            title="Featured Hero Graphic (JPG)"
            url={HERO_BANNER}
            note="3-phone Safe to Spend 365 marketing hero — press-ready"
          />
          <AssetCard
            title="Founder Headshot"
            url={FOUNDER_PHOTOS[0]?.src}
            note="High-resolution PNG"
          />
          <AssetCard
            title="App Screenshots — index"
            url="#screenshots"
            note="Right-click each screenshot above to save"
            internal
          />
        </div>
        <p className="mt-4 text-[12.5px] text-white/60">
          Individual assets — right-click → Save As. For a full zip, email {COMPANY.media_contact.email}.
        </p>
      </Section>

      {/* PRESS RELEASE */}
      <Section id="press-release" eyebrow="Press release" title={PRESS_RELEASE.headline}>
        <div className="mt-6 flex items-center gap-3 text-[13px] text-slate-500">
          <span>{PRESS_RELEASE.city}</span>
          <span>·</span>
          <span>{PRESS_RELEASE.date}</span>
          <button
            onClick={() => copy("Press release", PRESS_RELEASE.body)}
            data-testid="press-release-copy"
            className="ml-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[12.5px] hover:border-[#0A1128] transition-colors"
          >
            <Copy className="h-3 w-3" /> Copy full text
          </button>
        </div>
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-8 md:p-10 whitespace-pre-line text-[15px] text-slate-700 leading-relaxed af-shadow-card">
          {PRESS_RELEASE.body}
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section id="testimonials" eyebrow="User reviews" title="What early users are saying.">
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <blockquote
              key={i}
              className="rounded-2xl border border-slate-200 bg-white p-6 af-shadow-card"
            >
              <p className="text-[15px] text-[#0A1128] leading-relaxed">"{t.quote}"</p>
              <footer className="mt-4 text-[12.5px] text-slate-500 not-italic">— {t.author}</footer>
            </blockquote>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" eyebrow="For journalists & creators" title="Frequently asked questions.">
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="font-display text-[16px] text-[#0A1128]">{f.q}</p>
              <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" eyebrow="Contact" title="Get in touch." dark>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <a
            href={`mailto:${COMPANY.media_contact.email}`}
            className="group rounded-3xl border border-white/10 bg-white/5 p-8 hover:bg-white/10 transition-colors"
            data-testid="press-contact-email"
          >
            <Mail className="h-5 w-5 text-[#00C48C] mb-4" />
            <p className="text-[12px] uppercase tracking-[0.18em] text-white/60">Email us</p>
            <p className="mt-2 font-display text-[18px]">{COMPANY.media_contact.email}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-white/80 group-hover:gap-2.5 transition-all">
              Draft an email <ArrowRight className="h-4 w-4" />
            </span>
          </a>
          <Link
            to="/contact"
            className="group rounded-3xl border border-white/10 bg-white/5 p-8 hover:bg-white/10 transition-colors"
            data-testid="press-contact-form"
          >
            <ArrowUpRight className="h-5 w-5 text-[#00C48C] mb-4" />
            <p className="text-[12px] uppercase tracking-[0.18em] text-white/60">Contact form</p>
            <p className="mt-2 font-display text-[18px]">Send a longer message</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-white/80 group-hover:gap-2.5 transition-all">
              Open the form <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </Section>
    </>
  );
}

// -------------------- helpers --------------------
function Section({ id, eyebrow, title, children, dark }) {
  return (
    <section
      id={id}
      className={dark ? "bg-[#0A1128] text-white" : "bg-white"}
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-20 md:py-24">
        <p className={`text-[12px] uppercase tracking-[0.18em] ${dark ? "text-white/60" : "text-slate-500"}`}>
          {eyebrow}
        </p>
        <h2
          className={`font-display mt-2 text-[32px] sm:text-[40px] leading-[1.1] max-w-3xl ${dark ? "text-white" : "text-[#0A1128]"}`}
        >
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

function FactRow({ label, value }) {
  const isUrl = typeof value === "string" && value.startsWith("http");
  return (
    <div className="flex items-start gap-3">
      <dt className="text-slate-500 min-w-[120px]">{label}</dt>
      <dd className="text-[#0A1128] font-medium">
        {isUrl ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="hover:underline">
            {value.replace(/^https?:\/\//, "")}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function SocialRow({ href, label, Icon }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex items-center gap-3 hover:text-[#00C48C] transition-colors"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 group-hover:border-[#00C48C]">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[14px]">{label}</span>
        <ArrowUpRight className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
      </a>
    </li>
  );
}

function AssetCard({ title, url, note, internal }) {
  const Comp = internal ? "a" : "a";
  const extProps = internal
    ? { href: url }
    : { href: url, target: "_blank", rel: "noopener noreferrer", download: true };
  return (
    <Comp
      {...extProps}
      className="group rounded-2xl border border-white/10 bg-white/5 p-6 hover:bg-white/10 transition-colors block"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-[15px] text-white">{title}</p>
          <p className="mt-1 text-[12.5px] text-white/60">{note}</p>
        </div>
        <Download className="h-4 w-4 text-[#00C48C] group-hover:translate-y-[-2px] transition-transform" />
      </div>
    </Comp>
  );
}
